"use client";

import { useRef } from "react";
import type { ReactNode } from "react";
import { Bot, Braces, Radio, ScrollText, Webhook } from "lucide-react";
import {
  Button,
  DARK_BAND,
  SectionHead,
  SectionShell,
  useBandRowAlign,
  useWholeRowBlocks,
} from "@/components/sections/kit";
import { useEnterStagger } from "@/components/sections/kit/motion";
import { OPT_TILES, type TilePill } from "./data";
import { routePath } from "@/lib/routes";
import { roadmapItem } from "@/lib/roadmap";

const WEBHOOKS_ETA = roadmapItem("webhooks").promised;

/** Tile marks, tinted with the page's channel tokens (dark tiles). */
const TILE_ICONS: Record<string, ReactNode> = {
  "Request logs": <ScrollText size={16} strokeWidth={1.8} />,
  MCP: <Bot size={16} strokeWidth={1.8} />,
  "REST API": <Braces size={16} strokeWidth={1.8} />,
  "Click stream": <Radio size={16} strokeWidth={1.8} />,
  Webhooks: <Webhook size={16} strokeWidth={1.8} />,
};

const PILLS: Record<TilePill, { label: string; cls: string }> = {
  live: {
    label: "LIVE",
    cls: "bg-data-green/[0.14] text-data-green [border-color:color-mix(in_srgb,var(--color-data-green)_35%,transparent)]",
  },
  // Roadmap tag — the label is the tile's own lib/roadmap.ts date (pillLabel).
  roadmap: {
    label: "",
    cls: "bg-data-amber/[0.14] text-data-amber [border-color:color-mix(in_srgb,var(--color-data-amber)_35%,transparent)]",
  },
};

/** A roadmap tile's pill is its own roadmap item's promise, never hand-typed. */
function pillLabel(t: (typeof OPT_TILES)[number]): string {
  if (t.pill !== "roadmap") return PILLS[t.pill].label;
  if (!t.roadmap) throw new Error(`roadmap tile "${t.name}" needs a roadmap id`);
  return roadmapItem(t.roadmap).promised.toUpperCase();
}

/**
 * Optimize — the honest read-out row on the dark band. Five tiles, real
 * status on each: request logs and MCP reach every link; the REST API and
 * click stream cover API-created links; outbound webhooks are a dated
 * roadmap tag. No GA4 / Meta / Zapier / Slack connectors exist, so none are
 * shown.
 */
export default function OptimizeSection() {
  const rootRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useEnterStagger(rootRef, "[data-opt-tile]", { y: 22, stagger: 0.07 });

  return (
    <SectionShell
      id="optimize"
      band="dark"
      decoAvoid
      className="scroll-mt-[var(--grid-cell,72px)] max-md:bg-[#14141c] md:pt-px md:pb-px"
    >
      <div ref={rootRef} className="flex flex-col">
        <SectionHead
          eyebrow="Optimize"
          tone="light"
          gridFill={DARK_BAND}
          title="Put the numbers to work."
          lead={`Ask your AI agent for any link's numbers over MCP, or build on the REST API and its live click stream for links you create there. No native GA4 or Meta connectors yet — outbound webhooks are on the roadmap for ${WEBHOOKS_ETA}.`}
        />

        <div
          data-grid-block
          data-grid-fill={DARK_BAND}
          className="grid gap-3 max-md:mt-10 sm:grid-cols-2 lg:grid-cols-5"
        >
          {OPT_TILES.map((t) => (
            <div
              key={t.name}
              data-opt-tile
              className="flex flex-col gap-2.5 rounded-ref border border-white/[0.12] bg-white/[0.03] p-[18px]"
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-white/[0.16]"
                  style={{ color: t.color }}
                >
                  {TILE_ICONS[t.name]}
                </span>
                <span
                  className={`whitespace-nowrap rounded-full border px-2.5 py-[3px] font-jetbrains text-[9.5px] tracking-[0.06em] ${PILLS[t.pill].cls}`}
                >
                  {pillLabel(t)}
                </span>
              </div>
              <div className="text-[14px] font-bold text-white">{t.name}</div>
              <p className="m-0 text-[12px] leading-[1.5] text-[#a6a6b8]">{t.desc}</p>
            </div>
          ))}
        </div>

        <div
          data-grid-block
          data-grid-fill={DARK_BAND}
          className="flex flex-wrap items-center gap-5 max-md:mt-10"
        >
          <Button variant="ghost-light" href={routePath("integrations")}>
            Browse the catalog
          </Button>
          <span className="font-jetbrains text-[11px] text-[#73738a]">
            MCP on every plan · API 60 req/min Free, 600 Premium
          </span>
        </div>
      </div>
    </SectionShell>
  );
}
